import React, { useState } from "react"
import DineroFactory from "dinero.js"
import { BundleOutcome, Order, OrderLine } from "lib/types/generated/graphql-types"

import {
  BaseOrderLabel,
  BaseOrderValue,
  BaseOrderValueRow,
  BundleDiscountLabel,
  BundleRow,
  DiscountContainer,
  DiscountLabel,
  DiscountRow,
  DiscountValue,
  OrderValueRow,
  TotalDiscountLabel,
  TotalDiscountRow,
  TotalDiscountValue,
  Value
} from "./OrderDiscount.styled"

import Money from "../../../../../components/Money"
import BundleDiscount from "../../../../../components/Order/Discount/BundleDiscount"
import { ReactComponent as DownIcon } from "images/icons/chevron-down.svg"
import { ReactComponent as UpIcon } from "images/icons/chevron-up.svg"

type Props = {
  data: Order
}

const OrderDiscount = ({ data }: Props) => {
  const [bundlesExpanded, setBundlesExpanded] = useState(false)
  const {
    totals,
    currencyCode,
    discountCodeRules,
    discountRules,
    orderLines,
    discountExternalRules
  } = data

  const discountCartRuleAmount =
    discountRules?.reduce((acc, { discountAmount }) => acc + (discountAmount ?? 0), 0) ?? 0
  const discountCodeAmount =
    discountCodeRules?.reduce((acc, { discountAmount }) => acc + (discountAmount ?? 0), 0) ?? 0
  const discountExternalRuleAmount =
    discountExternalRules?.reduce((acc, { discountAmount }) => acc + (discountAmount ?? 0), 0) ?? 0

  type BundleOrderLine = {
    orderLine: OrderLine
    bundles: BundleOutcome[]
  }

  const baseOrderValue = (totals?.grandTotal ?? 0) + (totals?.discountTotal ?? 0)

  const bundleOrderLines = orderLines
    ?.flatMap(
      (orderLine) =>
        ({
          orderLine: orderLine,
          bundles: orderLine.discountOutcome.bundles
        }) as BundleOrderLine
    )
    .filter((orderLine) => orderLine.bundles.length > 0)

  const discountBundlesAmountArray = bundleOrderLines?.flatMap(
    (orderLine) =>
      orderLine.bundles.reduce((acc, { discountAmount }) => acc + (discountAmount ?? 0), 0) ?? 0
  )

  const discountBundlesAmount = discountBundlesAmountArray?.reduce((acc, a) => acc + a, 0) ?? 0

  const productDiscount =
    totals.discountTotal -
    (discountCartRuleAmount +
      discountCodeAmount +
      discountExternalRuleAmount +
      discountBundlesAmount)

  return (
    <DiscountContainer>
      <BaseOrderValueRow>
        <BaseOrderLabel>Base order value</BaseOrderLabel>
        <BaseOrderValue>
          <Money amount={baseOrderValue} currencyUnit={currencyCode as DineroFactory.Currency} />
        </BaseOrderValue>
      </BaseOrderValueRow>

      {discountRules &&
        discountRules.length > 0 &&
        discountRules.map((discountRule) => (
          <DiscountRow key={discountRule.id}>
            <DiscountLabel>
              Cart rule discount
              <br />
              <span>
                {discountRule.id} <Value>{discountRule.name}</Value>
              </span>
            </DiscountLabel>
            <DiscountValue>
              <Money
                amount={parseInt(`${discountRule.discountAmount}`)}
                currencyUnit={currencyCode as DineroFactory.Currency}
              />
            </DiscountValue>
          </DiscountRow>
        ))}

      {discountCodeRules &&
        discountCodeRules.length > 0 &&
        discountCodeRules.map((discountCodeRule) => (
          <DiscountRow key={discountCodeRule.id}>
            <DiscountLabel>
              Code rule discount
              <br />
              <span>
                {" "}
                {discountCodeRule.code} <Value>{discountCodeRule.name}</Value>
              </span>
            </DiscountLabel>
            <DiscountValue>
              <Money
                amount={parseInt(`${discountCodeRule.discountAmount}`)}
                currencyUnit={currencyCode as DineroFactory.Currency}
              />
            </DiscountValue>
          </DiscountRow>
        ))}

      {discountExternalRules &&
        discountExternalRules.length > 0 &&
        discountExternalRules.map((discountRule) => (
          <DiscountRow key={discountRule.id}>
            <DiscountLabel>
              External code rule discount
              <br />
              <span>
                {discountRule.id} <Value>{discountRule.name}</Value>
              </span>
            </DiscountLabel>
            <DiscountValue>
              <Money
                amount={parseInt(`${discountRule.discountAmount}`)}
                currencyUnit={currencyCode as DineroFactory.Currency}
              />
            </DiscountValue>
          </DiscountRow>
        ))}
      {productDiscount > 0 && (
        <DiscountRow>
          <DiscountLabel>
            Product discount
            <br />
          </DiscountLabel>
          <DiscountValue>
            <Money
              amount={parseInt(`${productDiscount}`)}
              currencyUnit={currencyCode as DineroFactory.Currency}
            />
          </DiscountValue>
        </DiscountRow>
      )}
      {bundleOrderLines && bundleOrderLines?.length > 0 && (
        <BundleRow>
          <span>
            <BundleDiscountLabel onClick={() => setBundlesExpanded(!bundlesExpanded)}>
              Bundle discount
              {bundlesExpanded ? <UpIcon /> : <DownIcon />}
            </BundleDiscountLabel>
            <DiscountValue>
              <Money
                amount={parseInt(`${discountBundlesAmount}`)}
                currencyUnit={currencyCode as DineroFactory.Currency}
              />
            </DiscountValue>
          </span>
          {bundlesExpanded &&
            bundleOrderLines.map((bundleOrderLine) => (
              <BundleDiscount
                bundleOrderLine={bundleOrderLine}
                currencyCode={currencyCode}
                key={bundleOrderLine.orderLine.id}
              />
            ))}
        </BundleRow>
      )}
      <TotalDiscountRow>
        <TotalDiscountLabel>Total discount</TotalDiscountLabel>
        <TotalDiscountValue>
          <Money
            amount={parseInt(`${totals?.discountTotal}`)}
            currencyUnit={currencyCode as DineroFactory.Currency}
          />
        </TotalDiscountValue>
      </TotalDiscountRow>

      <OrderValueRow>
        <BaseOrderLabel>Order value after discounts:</BaseOrderLabel>
        <BaseOrderValue>
          <Money
            amount={totals?.grandTotal ?? 0}
            currencyUnit={currencyCode as DineroFactory.Currency}
          />
        </BaseOrderValue>
      </OrderValueRow>
    </DiscountContainer>
  )
}

export default OrderDiscount
